import React, { useState, useEffect, useRef, useCallback } from "react";
import { api } from "../api";
import "./ActionPlanPanel.css";

interface ActionItem {
  id: number;
  cost_center: string;
  fiscal_year: string;
  month: string;
  action_text: string;
  category: string | null;
  status: "OPEN" | "CLOSED";
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

interface ActionPlanPanelProps {
  open: boolean;
  onClose: () => void;
  costCenterNumber: string;
  costCenterName?: string;
  fiscalYear: string;
  month: string;
  categories: string[];
}

const MAX_ACTIONS = 5;
const CATEGORIES = ["COMPENSATION", "OCCUPANCY", "OPERATING", "ADVERTISING", "ALLOCATED"];

const ActionPlanPanel: React.FC<ActionPlanPanelProps> = ({
  open, onClose, costCenterNumber, costCenterName, fiscalYear, month, categories,
}) => {
  const [actions, setActions] = useState<ActionItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [copied, setCopied] = useState(false);
  const debounceTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  const fetchActions = useCallback(async () => {
    if (!costCenterNumber || !fiscalYear || !month) return;
    setLoading(true);
    try {
      const res = await api.get("/action-plans", {
        params: { cost_center: costCenterNumber, fy: fiscalYear, month },
      });
      setActions(res.data);
    } catch (err) {
      console.error("Failed to fetch action plans:", err);
    } finally {
      setLoading(false);
    }
  }, [costCenterNumber, fiscalYear, month]);

  useEffect(() => {
    if (open) fetchActions();
  }, [open, fetchActions]);

  useEffect(() => {
    const timers = debounceTimers.current;
    return () => { Object.values(timers).forEach(clearTimeout); };
  }, []);

  const handleAdd = async () => {
    if (actions.length >= MAX_ACTIONS || adding) return;
    setAdding(true);
    try {
      const res = await api.post("/action-plans", {
        cost_center: costCenterNumber,
        fiscal_year: fiscalYear,
        month,
        action_text: "",
      });
      setActions((prev) => {
        if (prev.length >= MAX_ACTIONS) return prev;
        return [...prev, res.data];
      });
    } catch (err: any) {
      if (err?.response?.status === 400) {
        fetchActions(); // Re-sync if backend rejected
      }
    } finally {
      setAdding(false);
    }
  };

  const handleUpdate = async (id: number, field: string, value: string) => {
    setActions((prev) => prev.map((a) => (a.id === id ? { ...a, [field]: value } : a)));
    try {
      await api.put(`/action-plans/${id}`, { [field]: value });
    } catch {
      fetchActions();
    }
  };

  const handleTextChange = (id: number, value: string) => {
    setActions((prev) => prev.map((a) => (a.id === id ? { ...a, action_text: value } : a)));
    if (debounceTimers.current[id]) clearTimeout(debounceTimers.current[id]);
    debounceTimers.current[id] = setTimeout(() => {
      api.put(`/action-plans/${id}`, { action_text: value.trim() }).catch(() => fetchActions());
    }, 600);
  };

  const handleDelete = async (id: number) => {
    setActions((prev) => prev.filter((a) => a.id !== id));
    try {
      await api.delete(`/action-plans/${id}`);
    } catch {
      fetchActions();
    }
  };

  const handleToggleStatus = (action: ActionItem) => {
    handleUpdate(action.id, "status", action.status === "OPEN" ? "CLOSED" : "OPEN");
  };

  const handleShare = () => {
    const openActions = actions.filter((a) => a.status === "OPEN");
    const closedActions = actions.filter((a) => a.status === "CLOSED");
    let text = `Action Plan — ${month} ${fiscalYear}\nCC ${costCenterNumber}`;
    if (costCenterName) text += ` — ${costCenterName}`;
    text += "\n";
    if (openActions.length > 0) {
      text += "\nOPEN:\n";
      openActions.forEach((a, i) => {
        text += `  ${i + 1}. ${a.action_text || "(no description)"}`;
        if (a.category) text += ` [${a.category}]`;
        text += "\n";
      });
    }
    if (closedActions.length > 0) {
      text += "\nCLOSED:\n";
      closedActions.forEach((a, i) => {
        text += `  ${i + 1}. ${a.action_text || "(no description)"}`;
        if (a.category) text += ` [${a.category}]`;
        text += "\n";
      });
    }
    if (actions.length === 0) text += "\nNo actions recorded.\n";
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (!open) return null;

  const openCount = actions.filter((a) => a.status === "OPEN").length;
  const closedCount = actions.filter((a) => a.status === "CLOSED").length;
  const catOptions = categories.length > 0 ? categories : CATEGORIES;
  const atLimit = actions.length >= MAX_ACTIONS;

  return (
    <>
      <div className="ap-overlay" onClick={onClose} />
      <div className="ap-panel">
        {/* ── Header ─────────────────────────────────────────────── */}
        <div className="ap-header">
          <div className="ap-header-top">
            <div>
              <div className="ap-header-eyebrow">Action Plan</div>
              <div className="ap-title">{month} {fiscalYear}</div>
              <div className="ap-subtitle">
                CC {costCenterNumber}{costCenterName ? ` — ${costCenterName}` : ""}
              </div>
            </div>
            <button className="ap-close" onClick={onClose}>
              <span className="material-icons">close</span>
            </button>
          </div>
          <div className="ap-stats">
            <div className="ap-stat">
              <span className="ap-stat-val">{actions.length}</span>
              <span className="ap-stat-label">of {MAX_ACTIONS}</span>
            </div>
            {openCount > 0 && (
              <div className="ap-stat ap-stat--open">
                <span className="ap-stat-val">{openCount}</span>
                <span className="ap-stat-label">open</span>
              </div>
            )}
            {closedCount > 0 && (
              <div className="ap-stat ap-stat--closed">
                <span className="ap-stat-val">{closedCount}</span>
                <span className="ap-stat-label">closed</span>
              </div>
            )}
          </div>
        </div>

        {/* ── Body ───────────────────────────────────────────────── */}
        <div className="ap-body">
          {loading && actions.length === 0 && (
            <div className="ap-empty">Loading...</div>
          )}
          {!loading && actions.length === 0 && (
            <div className="ap-empty">
              <span className="material-icons ap-empty-icon">fact_check</span>
              <div className="ap-empty-text">No actions for {month}</div>
              <div className="ap-empty-hint">Add up to {MAX_ACTIONS} action items to track</div>
            </div>
          )}
          {actions.map((action, idx) => (
            <div
              key={action.id}
              className={`ap-card ${action.status === "CLOSED" ? "ap-card--closed" : ""}`}
            >
              <div className="ap-card-top">
                <span className="ap-card-num">{idx + 1}</span>
                <button
                  className={`ap-status ap-status--${action.status.toLowerCase()}`}
                  onClick={() => handleToggleStatus(action)}
                  title={`Click to ${action.status === "OPEN" ? "close" : "reopen"}`}
                >
                  <span className="material-icons" style={{ fontSize: 13 }}>
                    {action.status === "OPEN" ? "radio_button_unchecked" : "check_circle"}
                  </span>
                  {action.status}
                </button>
                <button className="ap-delete" onClick={() => handleDelete(action.id)} title="Delete">
                  <span className="material-icons">close</span>
                </button>
              </div>
              <textarea
                className="ap-card-textarea"
                value={action.action_text}
                onChange={(e) => handleTextChange(action.id, e.target.value)}
                placeholder="Describe the action..."
                rows={2}
                maxLength={500}
              />
              <div className="ap-card-meta">
                <select
                  className="ap-category"
                  value={action.category || ""}
                  onChange={(e) => handleUpdate(action.id, "category", e.target.value || "")}
                >
                  <option value="">No category</option>
                  {catOptions.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                {action.created_by && (
                  <span className="ap-card-author">{action.created_by}</span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* ── Footer ─────────────────────────────────────────────── */}
        <div className="ap-footer">
          <button
            className="ap-add-btn"
            onClick={handleAdd}
            disabled={atLimit || adding}
          >
            <span className="material-icons">add</span>
            {adding ? "Adding..." : atLimit ? `${MAX_ACTIONS} / ${MAX_ACTIONS} Actions` : "Add Action"}
          </button>
          {actions.length > 0 && (
            <button className="ap-share-btn" onClick={handleShare}>
              <span className="material-icons">{copied ? "check" : "content_copy"}</span>
              {copied ? "Copied" : "Copy for Meeting"}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default ActionPlanPanel;
